import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { LockStatus, SecurityConfig } from '@life-os/app-core';
import { shouldAutoLock } from '@life-os/app-core';
import { useServices, useStore } from './store.js';
import { useAsync } from '../lib/useAsync.js';

/**
 * Gates the whole app behind the local lock. When a passphrase is set and the
 * session is locked, the UI is replaced by an unlock prompt. When no passphrase
 * is set (status 'unset') or the session is unlocked, children render normally.
 * Also enforces idle auto-lock based on the configured idle minutes.
 */
export function LockGate({ children }: { children: ReactNode }) {
  const services = useServices();
  const { revision, refresh } = useStore();
  const status = useAsync<LockStatus>(() => services.security.status(), [revision]);
  const config = useAsync<SecurityConfig>(() => services.security.getConfig(), [revision]);

  const [pass, setPass] = useState('');
  const [error, setError] = useState('');
  const lastActivity = useRef<number>(Date.now());

  const st = status.value;
  const autoLockMinutes = config.value?.autoLockMinutes ?? 0;
  const locked = st === 'locked';

  // Track activity and auto-lock when idle (only when a lock is active).
  useEffect(() => {
    if (st !== 'unlocked' || autoLockMinutes <= 0) return;
    const bump = () => {
      lastActivity.current = Date.now();
    };
    const events: Array<keyof WindowEventMap> = ['pointerdown', 'keydown', 'pointermove'];
    for (const e of events) window.addEventListener(e, bump);
    const interval = setInterval(() => {
      if (shouldAutoLock(new Date(lastActivity.current).toISOString(), new Date().toISOString(), autoLockMinutes)) {
        void services.security.lock('auto').then(refresh);
      }
    }, 15_000);
    return () => {
      for (const e of events) window.removeEventListener(e, bump);
      clearInterval(interval);
    };
  }, [st, autoLockMinutes, services, refresh]);

  if (!locked) return <>{children}</>;

  const unlock = async () => {
    setError('');
    const ok = await services.security.unlock(pass);
    if (!ok) {
      setError('Incorrect passphrase.');
      return;
    }
    setPass('');
    lastActivity.current = Date.now();
    refresh();
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        display: 'grid',
        placeItems: 'center',
        background: 'var(--lc-surface, #0b0f0d)',
        padding: 24,
        zIndex: 1000,
      }}
    >
      <div style={{ width: 'min(360px, 92vw)', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <div style={{ fontSize: '1.4rem', fontWeight: 700 }}>Life OS is locked</div>
          <div style={{ color: 'var(--lc-on-surface-variant)', fontSize: 'var(--lc-text-sm)', marginTop: 4 }}>
            Enter your passphrase to continue. Everything stays on this device.
          </div>
        </div>
        <input
          type="password"
          value={pass}
          autoFocus
          onChange={(e) => setPass(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void unlock();
          }}
          placeholder="Passphrase"
          aria-label="Passphrase"
          style={{
            background: 'var(--lc-surface-container)',
            color: 'var(--lc-on-surface)',
            border: '1px solid var(--lc-outline-variant)',
            borderRadius: 'var(--lc-radius-full)',
            padding: '10px 16px',
            font: 'inherit',
            textAlign: 'center',
          }}
        />
        {error ? <div style={{ color: 'var(--lc-error, #e06)', fontSize: 'var(--lc-text-sm)' }}>{error}</div> : null}
        <button
          type="button"
          onClick={() => void unlock()}
          style={{
            background: 'var(--lc-primary)',
            color: 'var(--lc-on-primary)',
            border: 'none',
            borderRadius: 'var(--lc-radius-full)',
            padding: '10px 16px',
            font: 'inherit',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Unlock
        </button>
      </div>
    </div>
  );
}
